/**
 * AlertBanner
 *
 * Persistent top banner for critical/high security alerts.
 * Fixed below TopBar, only visible when active alerts exist.
 * Dismissible per session.
 */

import { X, Shield } from 'lucide-react';
import { useSecurityDashboard } from '../../hooks/useSecurity';
import { useSecurityStore } from '../../store/securityStore';
import { useAuthStore } from '../../store/authStore';
import { ADMIN_ROLES } from '../../constants/roles';

export function AlertBanner() {
  const { data } = useSecurityDashboard();
  const { dismissedAlertIds, dismissAlert } = useSecurityStore();
  const role = useAuthStore((s) => s.user?.role);

  const isAdmin = role ? (ADMIN_ROLES as readonly string[]).includes(role) : false;

  const banner = data?.alert_banner;

  // Admin-tier only; also skip when no active banner or dismissed this session.
  if (!isAdmin || !banner?.active || !banner.alert_id || dismissedAlertIds.has(banner.alert_id)) {
    return null;
  }

  const isCritical = banner.severity === 'critical';

  return (
    <div className={`px-4 py-2.5 flex items-center gap-3 ${
      isCritical
        ? 'bg-ems-red/10 border-b border-ems-red/20'
        : 'bg-ems-amber/10 border-b border-ems-amber/20'
    }`}>
      <div className={`flex items-center gap-1.5 ${
        isCritical ? 'text-ems-red' : 'text-ems-amber'
      }`}>
        <Shield className="w-4 h-4" />
        <span className={`text-xs font-bold uppercase px-1.5 py-0.5 rounded ${
          isCritical ? 'bg-ems-red/20 text-ems-red' : 'bg-ems-amber/20 text-ems-amber'
        }`}>
          {banner.severity}
        </span>
      </div>

      <div className="flex-1 min-w-0">
        <span className={`text-sm font-medium ${
          isCritical ? 'text-ems-red' : 'text-ems-amber'
        }`}>
          {banner.message}
        </span>
        {banner.detected_at && (
          <span className={`text-xs ml-2 ${
            isCritical ? 'text-ems-red/70' : 'text-ems-amber/70'
          }`}>
            {new Date(banner.detected_at).toLocaleString()}
          </span>
        )}
      </div>

      <div className="flex items-center gap-2">
        <a
          href="/security"
          className={`text-xs font-medium underline ${
            isCritical ? 'text-ems-red hover:text-ems-red/80' : 'text-ems-amber hover:text-ems-amber/80'
          }`}
        >
          View Details
        </a>
        <button
          onClick={() => banner.alert_id && dismissAlert(banner.alert_id)}
          className={`p-1 rounded hover:bg-black/5 ${
            isCritical ? 'text-ems-red' : 'text-ems-amber'
          }`}
          title="Dismiss for this session"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
